if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.applyVariantMediaFilter(this.dataset.selectedMediaGroup || '');
      }

      onSlideChanged(event) {
        const currentMediaId = event.detail?.currentElement?.dataset?.mediaId;
        if (!currentMediaId) {
          this.updateViewerCounter();
          return;
        }

        if (!this.elements.thumbnails) {
          this.updateViewerCounter();
          return;
        }

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${currentMediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.updateViewerCounter();
      }

      setActiveMedia(mediaId, prepend) {
        const visibleMediaItems = this.getVisibleMediaItems();
        const activeMedia =
          visibleMediaItems.find((element) => element.dataset.mediaId === mediaId) || visibleMediaItems[0];
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${activeMedia.dataset.mediaId}"]`
            );
            if (!activeThumbnail) {
              this.elements.viewer.resetPages?.();
              return;
            }

            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (this.closest('quick-add-modal')) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.ensureActiveMediaLoaded(activeMedia);

        if (!this.elements.thumbnails) {
          this.updateViewerCounter();
          return;
        }

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        if (!activeThumbnail) {
          this.updateViewerCounter();
          return;
        }

        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.updateViewerCounter();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      ensureActiveMediaLoaded(activeItem) {
        if (!activeItem) return;

        const revealImages = activeItem.querySelectorAll('img.product__media-image-reveal');
        if (!revealImages.length) return;

        const markLoaded = (image) => {
          requestAnimationFrame(() => requestAnimationFrame(() => image.classList.add('is-loaded')));
        };

        revealImages.forEach((image) => {
          image.loading = 'eager';
          image.fetchPriority = 'high';

          if (image.complete && image.naturalWidth > 0) {
            markLoaded(image);
            return;
          }

          image.addEventListener(
            'load',
            () => {
              markLoaded(image);
            },
            { once: true }
          );
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      isDesktopThumbnailLayout() {
        return this.mql.matches && this.dataset.desktopLayout.includes('thumbnail');
      }

      getVisibleMediaItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []).filter((item) => !item.hidden);
      }

      getVisibleThumbnailItems() {
        return Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []).filter((item) => !item.hidden);
      }

      getPreferredVisibleMedia(visibleMediaItems, preferredMediaId = '', previousActiveMediaId = '') {
        return (
          visibleMediaItems.find((item) => item.dataset.mediaId === preferredMediaId) ||
          visibleMediaItems.find((item) => item.dataset.mediaId === previousActiveMediaId) ||
          visibleMediaItems[0] ||
          null
        );
      }

      syncSliderNavigationState(sliderComponent, visibleItemCount) {
        if (!sliderComponent?.prevButton || !sliderComponent?.nextButton) return;

        if (visibleItemCount < 2) {
          sliderComponent.prevButton.setAttribute('disabled', 'disabled');
          sliderComponent.nextButton.setAttribute('disabled', 'disabled');
          if (sliderComponent.currentPageElement) sliderComponent.currentPageElement.textContent = visibleItemCount ? '1' : '0';
          if (sliderComponent.pageTotalElement) sliderComponent.pageTotalElement.textContent = `${visibleItemCount}`;
          return;
        }

        const canUpdateSlider =
          Array.isArray(sliderComponent.sliderItemsToShow) &&
          sliderComponent.sliderItemsToShow.length >= 2 &&
          Number.isFinite(sliderComponent.sliderItemOffset) &&
          sliderComponent.sliderItemOffset > 0;

        if (!canUpdateSlider) {
          sliderComponent.prevButton.setAttribute('disabled', 'disabled');
          sliderComponent.nextButton.setAttribute('disabled', 'disabled');
          return;
        }

        sliderComponent.update?.();
      }

      applyVariantMediaFilter(selectedGroup = '', preferredMediaId = '') {
        const mediaItems = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
        if (!mediaItems.length) return;

        const thumbnailItems = Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []);
        const previousActiveMediaId = mediaItems.find((item) => item.classList.contains('is-active'))?.dataset?.mediaId || '';
        const groupedMediaItems = mediaItems.filter((item) => item.dataset.mediaGroup);
        const hasMatchingGroup =
          selectedGroup && groupedMediaItems.some((item) => item.dataset.mediaGroup === selectedGroup);
        const shouldFilter = Boolean(selectedGroup && hasMatchingGroup);

        const isVisible = (item) => {
          const itemGroup = item.dataset.mediaGroup || '';
          return !shouldFilter || itemGroup === selectedGroup;
        };

        const visibilityAlreadyApplied =
          mediaItems.every((item) => item.hidden === !isVisible(item)) &&
          thumbnailItems.every((item) => item.hidden === !isVisible(item));

        const visibleMediaItems = mediaItems.filter((item) => isVisible(item));
        const fallbackMedia = this.getPreferredVisibleMedia(visibleMediaItems, preferredMediaId, previousActiveMediaId);
        const activeThumbnail = fallbackMedia
          ? this.elements.thumbnails?.querySelector(`[data-target="${fallbackMedia.dataset.mediaId}"]`)
          : null;
        const activeThumbnailButton = activeThumbnail?.querySelector('button');
        const activeStateAlreadyApplied =
          (!fallbackMedia || fallbackMedia.classList.contains('is-active')) &&
          (!activeThumbnailButton || activeThumbnailButton.hasAttribute('aria-current'));

        if (visibilityAlreadyApplied && activeStateAlreadyApplied && this.dataset.selectedMediaGroup === selectedGroup) {
          this.dataset.visibleMediaCount = `${visibleMediaItems.length}`;
          this.syncSliderNavigationState(this.elements.viewer, visibleMediaItems.length);
          this.syncSliderNavigationState(this.elements.thumbnails, this.getVisibleThumbnailItems().length);
          if (fallbackMedia) this.ensureActiveMediaLoaded(fallbackMedia);
          this.updateViewerCounter();
          return;
        }

        mediaItems.forEach((item) => {
          const visible = isVisible(item);
          item.hidden = !visible;
          if (!visible) item.classList.remove('is-active');
        });

        thumbnailItems.forEach((item) => {
          const visible = isVisible(item);
          item.hidden = !visible;

          const button = item.querySelector('button');
          if (!button) return;

          button.tabIndex = visible ? 0 : -1;
          if (!visible) button.removeAttribute('aria-current');
        });

        if (this.isDesktopThumbnailLayout()) {
          // Desktop thumbnail layouts hide every non-active slide, so keep one visible active fallback.
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => item.classList.remove('is-active'));
          if (fallbackMedia) {
            fallbackMedia.classList.add('is-active');
          }
        }

        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
        this.syncSliderNavigationState(this.elements.viewer, visibleMediaItems.length);
        this.syncSliderNavigationState(this.elements.thumbnails, this.getVisibleThumbnailItems().length);

        if (fallbackMedia) {
          this.setActiveMedia(fallbackMedia.dataset.mediaId, false);
        } else {
          this.updateViewerCounter();
        }

        this.dataset.selectedMediaGroup = selectedGroup;
        this.dataset.visibleMediaCount = `${visibleMediaItems.length}`;
      }

      updateViewerCounter() {
        const currentElement = this.elements.viewer?.querySelector('.slider-counter--current');
        const totalElement = this.elements.viewer?.querySelector('.slider-counter--total');
        if (!currentElement || !totalElement) return;

        const visibleMediaItems = this.getVisibleMediaItems();
        const activeIndex = visibleMediaItems.findIndex((item) => item.classList.contains('is-active'));

        currentElement.textContent = visibleMediaItems.length ? `${Math.max(activeIndex, 0) + 1}` : '0';
        totalElement.textContent = `${visibleMediaItems.length}`;
      }
    }
  );
}
